import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

// /api/practice/stripe-account/* — Stripe Connect Standard, owned by
// admin-server per Phase 0a of docs/build-plans/pma-stripe-payments.md.
// PMA initiates onboarding from BillingSettingsPanel; Stripe redirects
// back to /settings/stripe/return.

export interface StripeAccountStatus {
    connected: boolean;
    id?: string;
    practice_id?: string;
    stripe_user_id?: string;
    charges_enabled?: boolean;
    payouts_enabled?: boolean;
    details_submitted?: boolean;
    connected_at?: string;
    disconnected_at?: string | null;
}

export interface StartOnboardingResult {
    url: string;
    expires_at: number;
    stripe_user_id: string;
}

export function useStripeAccount() {
    return useQuery({
        queryKey: ['stripe-account'],
        queryFn: async () => {
            const res = await api.get<StripeAccountStatus>('/practice/stripe-account');
            return res.data;
        },
    });
}

export function useStartStripeOnboarding() {
    return useMutation({
        mutationFn: async () => {
            const res = await api.post<StartOnboardingResult>(
                '/practice/stripe-account/onboarding-link',
                {
                    return_url:  `${window.location.origin}/settings/stripe/return`,
                    refresh_url: `${window.location.origin}/settings/stripe/refresh`,
                },
            );
            return res.data;
        },
    });
}

export function useRefreshStripeStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const res = await api.post<StripeAccountStatus>(
                '/practice/stripe-account/refresh-status',
            );
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['stripe-account'] }),
    });
}

export function useDisconnectStripe() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const res = await api.post<StripeAccountStatus>(
                '/practice/stripe-account/disconnect',
            );
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['stripe-account'] }),
    });
}
